import React, { ReactNode } from "react";
import { WagmiProvider, type Config } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createAppKit } from "@reown/appkit/react";

import { config, networks, projectId, wagmiAdapter, metadata } from "../config/appkit";

// Create a single query client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// Initialize AppKit synchronously at module level with error handling
// Only initialize if projectId is properly set
if (projectId && projectId !== "YOUR_PROJECT_ID_HERE" && projectId !== "temp-project-id") {
  try {
    createAppKit({
      adapters: [wagmiAdapter],
      projectId,
      networks,
      defaultNetwork: networks[0], // Use the first network as default (BSC or BSC Testnet)
      defaultAccountTypes: { eip155: "eoa" },
      metadata,
      features: {
        analytics: true, // Enable analytics
        email: false, // Disable email login for now
        socials: ["google", "x", "discord", "apple", "facebook"],
      },
      // Customize theme and appearance
      themeMode: "dark",
      themeVariables: {
        "--w3m-accent": "#1976d2",
        "--w3m-border-radius-master": "6px",
      },
      // Add options to improve stability
      allWallets: "SHOW", // Show all available wallets
      includeWalletIds: undefined, // Allow all wallets
      excludeWalletIds: undefined, // Don't exclude any wallets
      featuredWalletIds: [
        "c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96", // Metamask
        "8a0ee50d1f22f6651afcae7eb4253e52a3310b90af5daef78a8c4929a9bb99d4", // Binance
        "971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709", // OKX
      ],
      // termsConditionsUrl: "https://deathx-fun.notion.site/Terms-of-Service-24e1b048608b81fc9c24eba29f65900b",
      // privacyPolicyUrl: "https://deathx-fun.notion.site/Privacy-Policy-24e1b048608b812ea35ae89acf071640",
    });

    console.log(
      "AppKit initialized successfully with networks:",
      networks.map((n) => n.name)
    );
  } catch (error) {
    console.error("Failed to initialize AppKit:", error);
    console.warn("AppKit initialization failed - wallet functionality may be limited");
  }
} else {
  console.warn("AppKit not initialized: Please set REACT_APP_WALLETCONNECT_PROJECT_ID in .env file");
}

interface AppKitProviderProps {
  children: ReactNode;
}

export const AppKitProvider: React.FC<AppKitProviderProps> = ({ children }) => {
  return (
    <WagmiProvider config={config as Config}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </WagmiProvider>
  );
};

export default AppKitProvider;
